import { useEffect, useMemo, useState } from "react";
import { FaEye } from "react-icons/fa";
import { getScootyFeatureConfig } from "../../services/homeContentService";

const scootyImage =
 "/assets/scooty-feature-02.png";

const defaultFeatures = [
 {
  id: "display",
  label: "Smart Led\nDisplay",
  x: 41,
  y: 26,
  labelX: 17,
  labelY: 13,
  anchorX: 36,
  anchorY: 20,
  align: "right",
  active: true,
 },
 {
  id: "cup",
  label: "Handy Cup\nHolder",
  x: 32,
  y: 35,
  labelX: 12,
  labelY: 34,
  anchorX: 29,
  anchorY: 35,
  align: "right",
  active: true,
 },
 {
  id: "body",
  label: "Sturdy\nBody",
  x: 33,
  y: 46,
  labelX: 11,
  labelY: 46,
  anchorX: 29,
  anchorY: 47,
  align: "right",
  active: true,
 },
 {
  id: "foot",
  label: "Convenient Foot\nSpace",
  x: 40,
  y: 62,
  labelX: 12,
  labelY: 61,
  anchorX: 33,
  anchorY: 62,
  align: "right",
  active: true,
 },
 {
  id: "tires",
  label: "Durable Tires",
  x: 32,
  y: 78,
  labelX: 10,
  labelY: 84,
  anchorX: 28,
  anchorY: 80,
  align: "right",
  active: true,
 },
 {
  id: "stand",
  label: "Easy Stand",
  x: 47,
  y: 84,
  labelX: 35,
  labelY: 91,
  anchorX: 44,
  anchorY: 87,
  align: "center",
  active: true,
 },
 {
  id: "suspension",
  label: "Smooth\nSuspension",
  x: 54,
  y: 76,
  labelX: 57,
  labelY: 90,
  anchorX: 55,
  anchorY: 82,
  align: "center",
  active: true,
 },
 {
  id: "motor",
  label: "Efficient Motor",
  x: 61,
  y: 77,
  labelX: 78,
  labelY: 81,
  anchorX: 66,
  anchorY: 78,
  align: "left",
  active: true,
 },
 {
  id: "license",
  label: "No License &\nNo Registration",
  x: 67,
  y: 58,
  labelX: 79,
  labelY: 61,
  anchorX: 71,
  anchorY: 59,
  align: "left",
  active: true,
 },
 {
  id: "guard",
  label: "Protective\nGuard",
  x: 65,
  y: 43,
  labelX: 80,
  labelY: 39,
  anchorX: 69,
  anchorY: 43,
  align: "left",
  active: true,
 },
 {
  id: "comfort",
  label: "Comfort\nSpacious",
  x: 56,
  y: 39,
  labelX: 72,
  labelY: 16,
  anchorX: 60,
  anchorY: 34,
  align: "left",
  active: true,
 },
];

const labelAlignClass = {
 left: "text-left translate-x-3",
 right: "text-right -translate-x-[calc(100%+0.75rem)]",
 center: "text-center -translate-x-1/2",
};

function getFallbackArrowEnd(feature) {
 const gap = 1.6;
 const labelX = Number(feature.labelX ?? 50);
 const labelY = Number(feature.labelY ?? 50);
 if (feature.align === "left") return { x: Math.max(0, labelX - gap), y: labelY };
 if (feature.align === "right") return { x: Math.min(100, labelX + gap), y: labelY };
 return { x: labelX, y: labelY };
}

function getArrowEnd(feature) {
 const fallback = getFallbackArrowEnd(feature);
 const x = Number.isFinite(Number(feature.arrowX)) ? Number(feature.arrowX) : fallback.x;
 const y = Number.isFinite(Number(feature.arrowY)) ? Number(feature.arrowY) : fallback.y;
 return `${x},${y}`;
}

export default function ScootyFeatureShowcase() {
 const [config, setConfig] = useState({
  image: scootyImage,
  active: true,
  features: defaultFeatures,
 });
 const features = useMemo(
  () =>
   (config.features || defaultFeatures).filter(
    (feature) => feature.active !== false,
   ),
  [config.features],
 );
 const allFeatureIds = useMemo(
  () => features.map((feature) => feature.id),
  [features],
 );
 const [visibleFeatureIds, setVisibleFeatureIds] = useState(
  new Set(defaultFeatures.map((feature) => feature.id)),
 );
 const visibleCount = visibleFeatureIds.size;
 const allVisible = visibleCount === features.length;

 useEffect(() => {
  getScootyFeatureConfig().then((nextConfig) => {
   if (nextConfig) setConfig(nextConfig);
  });
 }, []);

 useEffect(() => {
  setVisibleFeatureIds(new Set(allFeatureIds));
 }, [allFeatureIds]);

 function showAllFeatures() {
  setVisibleFeatureIds(new Set(allFeatureIds));
 }

 function hideAllFeatures() {
  setVisibleFeatureIds(new Set());
 }

 function toggleFeature(featureId) {
  setVisibleFeatureIds((current) => {
   const next = new Set(current);
   if (next.has(featureId)) next.delete(featureId);
   else next.add(featureId);
   return next;
  });
 }

 if (config.active === false || features.length === 0) return null;

 return (
  <section className="bg-white py-12 sm:py-14" style={{ boxShadow: "none" }}>
   <div className="container-pad">
    <div className="mx-auto max-w-6xl overflow-hidden bg-white" style={{ boxShadow: "none" }}>
     <div className="flex items-center justify-center gap-2 px-4 pt-5 sm:pt-7">
      <button
       type="button"
       onClick={showAllFeatures}
       className={`inline-flex min-h-9 items-center gap-2 rounded-md px-4 py-2 text-xs font-bold transition ${
        allVisible
         ? "bg-[#54d52e] text-[#062B3A]"
         : "bg-white text-[#42566B] hover:bg-emerald-50 hover:text-primary"
       }`}
      >
       <FaEye aria-hidden="true" />
       View All Features
      </button>
      <button
       type="button"
       onClick={hideAllFeatures}
       className={`inline-flex min-h-9 items-center rounded-md px-4 py-2 text-xs font-bold transition ${
        visibleCount === 0
         ? "bg-[#54d52e] text-[#062B3A]"
         : "bg-white text-[#42566B] hover:bg-emerald-50 hover:text-primary"
       }`}
      >
       Hide All
      </button>
     </div>

     <div className="relative mx-auto aspect-[16/9] w-full max-w-[1080px] overflow-hidden bg-white px-3 py-4 sm:px-6 sm:py-6" style={{ boxShadow: "none" }}>
      <img
       src={config.image || scootyImage}
       alt="EV scooty"
       className="absolute inset-0 h-full w-full scale-[1.05] object-contain px-[10%] py-[5%] sm:px-[8%] sm:py-[4%]"
       loading="lazy"
      />

      <svg
       className="pointer-events-none absolute inset-0 hidden h-full w-full sm:block"
       viewBox="0 0 100 100"
       preserveAspectRatio="none"
       aria-hidden="true"
      >
       <defs>
        <marker
         id="scooty-arrow-head"
         markerHeight="12"
         markerWidth="12"
         orient="auto"
         refX="10"
         refY="5"
         viewBox="0 0 10 10"
        >
         <path d="M1,1 L10,5 L1,9" fill="none" stroke="#020617" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.9" />
        </marker>
       </defs>
       {features.map((feature) =>
        visibleFeatureIds.has(feature.id) ? (
         <polyline
          key={feature.id}
          points={`${feature.x},${feature.y} ${feature.anchorX},${feature.anchorY} ${getArrowEnd(feature)}`}
          fill="none"
          markerEnd="url(#scooty-arrow-head)"
          stroke="#020617"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth="3.1"
          vectorEffect="non-scaling-stroke"
         />
        ) : null,
       )}
      </svg>

      {features.map((feature) => {
       const isVisible = visibleFeatureIds.has(feature.id);
       return (
        <div key={feature.id}>
         {isVisible ? (
          <div
           className={`pointer-events-none absolute hidden max-w-[150px] whitespace-pre-line text-[15px] font-black leading-tight text-[#020617] sm:block lg:text-[17px] ${labelAlignClass[feature.align]}`}
           style={{ left: `${feature.labelX}%`, top: `${feature.labelY}%` }}
          >
           {feature.label}
          </div>
         ) : null}

         <button
          type="button"
          onClick={() => toggleFeature(feature.id)}
          className={`absolute z-10 grid h-6 w-6 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border-2 bg-white text-sm font-black leading-none transition hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary/35 ${
           isVisible
            ? "border-primary text-primary"
            : "border-slate-400 text-[#062B3A]"
          }`}
          style={{ left: `${feature.x}%`, top: `${feature.y}%` }}
          aria-label={`${isVisible ? "Hide" : "Show"} ${feature.label.replace(/\n/g, " ")}`}
          title={feature.label.replace(/\n/g, " ")}
         >
          {isVisible ? "-" : "+"}
         </button>
        </div>
       );
      })}
     </div>
    </div>
   </div>
  </section>
 );
}













